import type {
  SpanJSON,
  TraceContext,
  TransactionEvent,
  SerializedEvent,
} from '@sentry/types';
import type { Breadcrumb, Event } from '@sentry/react';

const httpMethods = [
  'GET',
  'HEAD',
  'POST',
  'PUT',
  'DELETE',
  'CONNECT',
  'OPTIONS',
  'TRACE',
  'PATCH',
];

const isHttpStatus = (value: unknown): value is number =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  (value === 0 || (value >= 100 && value <= 599));

export const sanitizeRequestMetadata = (
  metadata: Record<string, unknown> = {},
): Record<string, string | number | boolean> => {
  const safe: Record<string, string | number | boolean> = {};
  if (
    typeof metadata.method === 'string' &&
    httpMethods.includes(metadata.method.toUpperCase())
  ) {
    safe.method = metadata.method.toUpperCase();
  }
  if (isHttpStatus(metadata.status)) {
    safe.status = metadata.status;
  }
  if (
    typeof metadata.kind === 'string' &&
    ['http', 'network', 'invalid_response'].includes(metadata.kind)
  ) {
    safe.kind = metadata.kind;
  }
  if (typeof metadata.online === 'boolean') {
    safe.online = metadata.online;
  }
  return safe;
};

const breadcrumbCategories = [
  'fetch',
  'xhr',
  'http',
  'navigation',
  'console',
  'ui.click',
  'ui.input',
  'sentry.event',
  'sentry.transaction',
];

export const sanitizeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  const safe: Breadcrumb = {
    category:
      breadcrumb.category && breadcrumbCategories.includes(breadcrumb.category)
        ? breadcrumb.category
        : 'custom',
  };
  if (
    breadcrumb.level &&
    ['fatal', 'error', 'warning', 'log', 'info', 'debug'].includes(
      breadcrumb.level,
    )
  ) {
    safe.level = breadcrumb.level;
  }
  if (
    typeof breadcrumb.timestamp === 'number' &&
    Number.isFinite(breadcrumb.timestamp)
  ) {
    safe.timestamp = breadcrumb.timestamp;
  }
  if (['fetch', 'xhr', 'http'].includes(safe.category!)) {
    const metadata = sanitizeRequestMetadata({
      method: breadcrumb.data?.method,
      status: breadcrumb.data?.status_code,
    });
    safe.data = {
      ...(metadata.method !== undefined && { method: metadata.method }),
      ...(metadata.status !== undefined && { status_code: metadata.status }),
    };
  }
  return safe;
};

export const sanitizeErrorEvent = (event: Event): Event => {
  const { user, request, ...safe } = event;
  return {
    ...safe,
    extra: sanitizeRequestMetadata(event.extra),
    tags: sanitizeRequestMetadata(event.tags),
    ...(event.breadcrumbs && {
      breadcrumbs: event.breadcrumbs.map(sanitizeBreadcrumb),
    }),
  };
};

const sanitizeTraceContext = (
  trace: Pick<
    TraceContext,
    'trace_id' | 'span_id' | 'parent_span_id' | 'op' | 'status'
  >,
): TraceContext => ({
  trace_id: trace.trace_id,
  span_id: trace.span_id,
  parent_span_id: trace.parent_span_id,
  op: trace.op,
  status: trace.status,
});

const sanitizeSpan = (span: SpanJSON): SpanJSON => {
  const status = span.data?.['http.response.status_code'];
  return {
    ...sanitizeTraceContext(span),
    start_timestamp: span.start_timestamp,
    timestamp: span.timestamp,
    ...(isHttpStatus(status) && {
      data: { 'http.response.status_code': status },
    }),
  };
};

export const sanitizeTransaction = (
  event: TransactionEvent,
): TransactionEvent => {
  const safe = sanitizeErrorEvent(event);
  const { _metrics_summary, ...transaction } = safe as TransactionEvent;
  return {
    ...transaction,
    type: 'transaction',
    transaction: 'Browser transaction',
    transaction_info: { source: 'custom' },
    contexts: event.contexts?.trace
      ? { trace: sanitizeTraceContext(event.contexts.trace) }
      : {},
    // Sentry 7 serializes spans before this hook, although its hook type uses Span[].
    spans: (event as unknown as SerializedEvent).spans?.map(sanitizeSpan),
    sdkProcessingMetadata: {
      ...event.sdkProcessingMetadata,
      dynamicSamplingContext: {
        ...event.sdkProcessingMetadata?.dynamicSamplingContext,
        transaction: 'Browser transaction',
      },
    },
  } as unknown as TransactionEvent;
};
